import { FeatureFlagValue } from '@/common/domain/constants/featureFlags';
import { useFeatureFlags } from '@/common/presentation/hooks/useFeatureFlags';
import { useMemo } from 'react';

interface NavItem {
  featureFlag?: FeatureFlagValue;
  children?: NavItem[];
  [key: string]: unknown;
}

// Non-hook utility function for recursive filtering
const filterNavItemsByFeatureFlags = (items: NavItem[], getFeatureFlag: (flag?: FeatureFlagValue) => boolean): NavItem[] => {
  return items
    .map((item) => {
      // If item has children, recursively filter them
      if (item.children) {
        return {
          ...item,
          children: filterNavItemsByFeatureFlags(item.children, getFeatureFlag)
        };
      }
      return item;
    })
    .filter((item) => {
      if (item.featureFlag && !getFeatureFlag(item.featureFlag)) {
        return false;
      }
      // Drop empty collapse parents after children were filtered out (TROC-317 Customers/Job Sites).
      if (Array.isArray(item.children) && item.children.length === 0) {
        return false;
      }
      return true;
    });
};

export interface FilteredNavItemsResult {
  items: NavItem[];
  isReady: boolean;
}

export const useFilterNavItemsByFeatureFlags = (items: NavItem[]): FilteredNavItemsResult => {
  // 1. Collect all unique flags first
  const flagKeys = useMemo(
    () =>
      Array.from(
        new Set(items.flatMap((i) => (i.children ? [i.featureFlag, ...i.children.map((c) => c.featureFlag)] : [i.featureFlag])))
      ).filter(Boolean) as FeatureFlagValue[],
    [items]
  );

  // 2. Read all flags via a single hook to avoid calling hooks in loops
  const { values: flagValuesMap, isReady } = useFeatureFlags(flagKeys);

  // 3. Memoized filtering using a pure getter
  const filteredItems = useMemo(() => {
    if (!isReady) {
      return items;
    }
    const getFlag = (key?: FeatureFlagValue): boolean => (key ? (flagValuesMap[key] ?? false) : true);
    return filterNavItemsByFeatureFlags(items, getFlag);
  }, [items, flagValuesMap, isReady]);

  return {
    items: filteredItems,
    isReady
  };
};
